import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const GAUGE_WIDTH = 220;
const GAUGE_HEIGHT = 140;
const LARGE_GAUGE_WIDTH = 300;
const LARGE_GAUGE_HEIGHT = 180;

function getScoreColor(score) {
  if (score > 75) return '#16a34a';
  if (score >= 50) return '#f59e0b';
  return '#dc2626';
}

function getScoreGradientId(field) {
  return `stability-gradient-${field}`;
}

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '20px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginTop: '4px',
  },
  overallContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  gaugeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '16px',
    marginBottom: '20px',
  },
  gaugeCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: '#f9fafb',
    borderRadius: '8px',
    padding: '16px 8px 12px',
  },
  gaugeLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginTop: '4px',
    textAlign: 'center',
  },
  gaugeDetail: {
    fontSize: '11px',
    color: '#6b7280',
    marginTop: '2px',
    textAlign: 'center',
  },
  interpretation: {
    fontSize: '13px',
    color: '#4b5563',
    fontStyle: 'italic',
    lineHeight: 1.5,
    marginTop: '4px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '240px',
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '2px',
  },
  tooltipLabel: {
    color: '#6b7280',
  },
  tooltipValue: {
    fontWeight: 600,
    color: '#1f2937',
  },
  empty: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
    fontSize: '14px',
  },
};

function drawGauge(svgEl, gauge, width, height, isLarge, onMouseEnter, onMouseMove, onMouseLeave) {
  const cx = width / 2;
  const cy = height - 10;
  const outerRadius = isLarge ? 110 : 80;
  const arcWidth = isLarge ? 18 : 14;
  const innerRadius = outerRadius - arcWidth;

  const svg = d3.select(svgEl);
  svg.selectAll('*').remove();
  svg
    .attr('viewBox', `0 0 ${width} ${height}`)
    .attr('width', '100%')
    .attr('height', '100%')
    .style('max-width', `${width}px`);

  const defs = svg.append('defs');

  // Create gradient for the filled arc
  const gradientId = getScoreGradientId(gauge.field);
  const scoreColor = getScoreColor(gauge.score);
  const gradient = defs
    .append('linearGradient')
    .attr('id', gradientId)
    .attr('x1', '0%')
    .attr('y1', '0%')
    .attr('x2', '100%')
    .attr('y2', '0%');

  gradient
    .append('stop')
    .attr('offset', '0%')
    .attr('stop-color', d3.color(scoreColor).brighter(0.4).toString());

  gradient
    .append('stop')
    .attr('offset', '100%')
    .attr('stop-color', scoreColor);

  const g = svg.append('g').attr('transform', `translate(${cx}, ${cy})`);

  const arcGenerator = d3
    .arc()
    .innerRadius(innerRadius)
    .outerRadius(outerRadius)
    .cornerRadius(2);

  // Background arc (full semicircle)
  g.append('path')
    .attr(
      'd',
      arcGenerator({
        startAngle: -Math.PI / 2,
        endAngle: Math.PI / 2,
      })
    )
    .attr('fill', '#f3f4f6');

  // Filled arc based on score
  const scoreAngle = -Math.PI / 2 + (Math.PI * (gauge.score / 100));

  g.append('path')
    .attr(
      'd',
      arcGenerator({
        startAngle: -Math.PI / 2,
        endAngle: scoreAngle,
      })
    )
    .attr('fill', `url(#${gradientId})`);

  // Tick marks at 0, 25, 50, 75, 100
  const tickValues = [0, 25, 50, 75, 100];
  const tickOuter = outerRadius + 5;
  const tickInner = outerRadius + 1;
  const labelRadius = outerRadius + (isLarge ? 16 : 14);

  tickValues.forEach((val) => {
    const angle = -Math.PI / 2 + (Math.PI * (val / 100));
    const x1 = tickInner * Math.cos(angle);
    const y1 = tickInner * Math.sin(angle);
    const x2 = tickOuter * Math.cos(angle);
    const y2 = tickOuter * Math.sin(angle);

    g.append('line')
      .attr('x1', x1)
      .attr('y1', y1)
      .attr('x2', x2)
      .attr('y2', y2)
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1);

    const lx = labelRadius * Math.cos(angle);
    const ly = labelRadius * Math.sin(angle);

    g.append('text')
      .attr('x', lx)
      .attr('y', ly)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', isLarge ? '10px' : '8px')
      .attr('fill', '#9ca3af')
      .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
      .text(val);
  });

  // Score number in center
  g.append('text')
    .attr('x', 0)
    .attr('y', isLarge ? -38 : -28)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'middle')
    .attr('font-size', isLarge ? '32px' : '24px')
    .attr('font-weight', '700')
    .attr('fill', scoreColor)
    .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
    .text(gauge.score);

  g.append('text')
    .attr('x', 0)
    .attr('y', isLarge ? -14 : -10)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'middle')
    .attr('font-size', isLarge ? '13px' : '10px')
    .attr('fill', '#9ca3af')
    .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
    .text('/100');

  // Hover region (invisible rect covering the gauge area)
  g.append('rect')
    .attr('x', -outerRadius)
    .attr('y', -outerRadius)
    .attr('width', outerRadius * 2)
    .attr('height', outerRadius)
    .attr('fill', 'transparent')
    .style('cursor', 'pointer')
    .on('mouseenter', function (event) {
      onMouseEnter(event, gauge);
    })
    .on('mousemove', function (event) {
      onMouseMove(event, gauge);
    })
    .on('mouseleave', function () {
      onMouseLeave();
    });
}

export default function PriceStabilityGauge({ data }) {
  const overallSvgRef = useRef(null);
  const gaugeSvgRefs = useRef({});
  const [tooltip, setTooltip] = useState(null);

  const fieldGauges = useMemo(() => {
    if (!data || !data.gauges) return [];
    return data.gauges.filter((g) => g.field !== 'overall');
  }, [data]);

  const overallGauge = useMemo(() => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
  const emptyStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '320px',
    background: '#f9fafb',
    border: '1px dashed #e5e7eb',
    borderRadius: '8px',
    color: '#6b7280',
    fontSize: '14px',
    fontStyle: 'italic'
  };

  return <div style={emptyStyle}>No analytical data available for this selection.</div>;
}
    if (data.gauges) {
      const found = data.gauges.find((g) => g.field === 'overall');
      if (found) return found;
    }
    if (data.overallScore != null) {
      return {
        field: 'overall',
        score: data.overallScore,
        label: 'Overall Stability',
        changeCount: 0,
        avgChange: 0,
        maxChange: 0,
      };
    }
    return null;
  }, [data]);

  const handleMouseEnter = (event, gauge) => {
    setTooltip({
      x: event.clientX + 14,
      y: event.clientY - 14,
      field: gauge.label || gauge.field,
      score: gauge.score,
      changeCount: gauge.changeCount,
      avgChange: gauge.avgChange,
      maxChange: gauge.maxChange,
    });
  };

  const handleMouseMove = (event, gauge) => {
    setTooltip({
      x: event.clientX + 14,
      y: event.clientY - 14,
      field: gauge.label || gauge.field,
      score: gauge.score,
      changeCount: gauge.changeCount,
      avgChange: gauge.avgChange,
      maxChange: gauge.maxChange,
    });
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  // Draw overall gauge
  useEffect(() => {
    if (!overallGauge || !overallSvgRef.current) return;
    drawGauge(
      overallSvgRef.current,
      overallGauge,
      LARGE_GAUGE_WIDTH,
      LARGE_GAUGE_HEIGHT,
      true,
      handleMouseEnter,
      handleMouseMove,
      handleMouseLeave
    );
  }, [overallGauge]);

  // Draw individual field gauges
  useEffect(() => {
    fieldGauges.forEach((gauge) => {
      const ref = gaugeSvgRefs.current[gauge.field];
      if (!ref) return;
      drawGauge(
        ref,
        gauge,
        GAUGE_WIDTH,
        GAUGE_HEIGHT,
        false,
        handleMouseEnter,
        handleMouseMove,
        handleMouseLeave
      );
    });
  }, [fieldGauges]);

  if (!data) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Stability Index</div>
          <div style={styles.subtitle}>
            Stability scores across service tiers (100 = perfectly stable)
          </div>
        </div>
        <div style={styles.empty}>No data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Stability Index</div>
        <div style={styles.subtitle}>
          Stability scores across service tiers (100 = perfectly stable)
        </div>
      </div>

      {/* Overall gauge - prominent position */}
      {overallGauge && (
        <div style={styles.overallContainer}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <svg ref={overallSvgRef} />
            <div style={{ ...styles.gaugeLabel, fontSize: '14px', marginTop: '6px' }}>
              {overallGauge.label || 'Overall Stability'}
            </div>
            {overallGauge.changeCount != null && (
              <div style={styles.gaugeDetail}>
                {overallGauge.changeCount} change{overallGauge.changeCount !== 1 ? 's' : ''} detected
              </div>
            )}
          </div>
        </div>
      )}

      {/* Field gauges grid */}
      {fieldGauges.length > 0 && (
        <div style={styles.gaugeGrid}>
          {fieldGauges.map((gauge) => (
            <div key={gauge.field} style={styles.gaugeCell}>
              <svg
                ref={(el) => {
                  gaugeSvgRefs.current[gauge.field] = el;
                }}
              />
              <div style={styles.gaugeLabel}>{gauge.label || gauge.field}</div>
              <div style={styles.gaugeDetail}>
                {gauge.changeCount} change{gauge.changeCount !== 1 ? 's' : ''} detected
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Interpretation */}
      {data.interpretation && (
        <div style={styles.interpretation}>{data.interpretation}</div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              color: '#1f2937',
              marginBottom: '6px',
              fontSize: '13px',
            }}
          >
            {tooltip.field}
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipLabel}>Score</span>
            <span
              style={{
                ...styles.tooltipValue,
                color: getScoreColor(tooltip.score),
              }}
            >
              {tooltip.score}/100
            </span>
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipLabel}>Changes</span>
            <span style={styles.tooltipValue}>{tooltip.changeCount}</span>
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipLabel}>Avg Change</span>
            <span style={styles.tooltipValue}>
              {tooltip.avgChange != null ? `${tooltip.avgChange.toFixed(2)}%` : 'N/A'}
            </span>
          </div>
          <div style={{ ...styles.tooltipRow, marginBottom: 0 }}>
            <span style={styles.tooltipLabel}>Max Change</span>
            <span style={styles.tooltipValue}>
              {tooltip.maxChange != null ? `${tooltip.maxChange.toFixed(2)}%` : 'N/A'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
